import {
    Home as HomeIcon,
    MonitorHeart as MonitorHeartIcon,
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import TimelineIcon from '@mui/icons-material/Timeline';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EditTrackingService from '../../services/EditTrackingService';
import { PageHeader } from '../common/PageHeader';
import VideoBrowserDialog from '../dialogs/VideoBrowserDialog';
import EditingLeaderboard from './EditingLeaderboard';
import EditTrendsTab from './EditTrendsTab';
import FeatureUsageStats from './FeatureUsageStats';
import StageTransitionAnalytics from './StageTransitionAnalytics';
import UserEditStats from './UserEditStats';

const EditingStatsPage = ({ darkMode, toggleDarkMode, themeVariant }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [, setVideoId] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearingData, setClearingData] = useState(false);
  const [clearError, setClearError] = useState(null);
  const [clearSuccess, setClearSuccess] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  // Add new states for folder filtering
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderBrowserOpen, setFolderBrowserOpen] = useState(false);
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'folder'
  const [statsDateRange, setStatsDateRange] = useState('last30days');
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [languagesLoading, setLanguagesLoading] = useState(false);

  const loadVideoData = useCallback(async () => {
    setLoading(true);
    try {
      const videoProps = JSON.parse(localStorage.getItem('videoProps'));
      if (videoProps && videoProps.id) {
        setVideoId(videoProps.id);
      }
      
      const selectedLng = localStorage.getItem('selectedLng');
      void selectedLng;
      
      // Check if user is admin
      const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      setIsAdmin(authUser?.userType === 'admin' || authUser?.role === 'ADMIN');
      
    } catch (error) {
      console.error('Error loading video data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVideoData();
  }, [loadVideoData]);

  useEffect(() => {
    const loadLanguageOptions = async () => {
      setLanguagesLoading(true);
      try {
        const folderPath = filterMode === 'folder' && selectedFolder ? formatPath(selectedFolder.path) : null;
        const languages = await EditTrackingService.getUserLanguageBreakdown(folderPath, null, statsDateRange);
        const languageNames = (languages || [])
          .map((entry) => entry?.language)
          .filter((language) => typeof language === 'string' && language.trim().length > 0)
          .sort((a, b) => a.localeCompare(b));
        setAvailableLanguages(languageNames);
        setSelectedLanguage((prev) => (prev && languageNames.includes(prev) ? prev : ''));
      } catch (error) {
        console.error('Error loading language options:', error);
        setAvailableLanguages([]);
        setSelectedLanguage('');
      } finally {
        setLanguagesLoading(false);
      }
    };

    loadLanguageOptions();
  }, [filterMode, selectedFolder, statsDateRange]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    
    // Increment refresh key to force child components to re-render
    setRefreshKey(prevKey => prevKey + 1);
    
    // Reload video data
    loadVideoData().then(() => {
      // Wait a bit to ensure the refresh animation shows
      setTimeout(() => {
        setRefreshing(false);
      }, 1000);
    });
  };

  const handleOpenClearDialog = () => {
    setClearDialogOpen(true);
    setClearError(null);
    setClearSuccess(false);
  };

  const handleCloseClearDialog = () => {
    setClearDialogOpen(false);
  };

  const handleClearTrackingData = async () => {
    setClearingData(true);
    setClearError(null);
    
    try {
      // Call backend to clear tracking data
      await EditTrackingService.clearTrackingData();
      
      // Show success and close dialog after a delay
      setClearSuccess(true);
      setTimeout(() => {
        setClearDialogOpen(false);
        setClearSuccess(false);
        
        // Refresh data
        handleRefresh();
      }, 2000);
    } catch (error) {
      console.error('Failed to clear tracking data:', error);
      setClearError(error.response?.data?.message || 'Failed to clear tracking data. Please try again.');
    } finally {
      setClearingData(false);
    }
  };

  // Update the folder selection handler with more robust logging
  const handleFolderSelect = (folder) => {
    console.log('Selected folder:', folder); // Detailed logging
    
    if (folder) {
      // Handle various folder object structures
      const folderPath = folder.path || folder.folderPath || 
                        (typeof folder === 'string' ? folder : '');
      
      console.log('Extracted folder path:', folderPath);
      
      if (folderPath) {
        const folderObject = {
          path: folderPath,
          name: getFolderNameFromPath(folderPath)
        };
        
        console.log('Setting selected folder to:', folderObject);
        
        setSelectedFolder(folderObject);
        setFilterMode('folder');
        setFolderBrowserOpen(false);
      } else {
        console.error('Invalid folder structure:', folder);
      }
    }
  };

  // Helper function to get folder name from path
  const getFolderNameFromPath = (path) => {
    if (!path) return 'Root';
    const parts = path.split('/').filter(p => p);
    return parts.length > 0 ? parts[parts.length - 1] : 'Root';
  };

  // Update the formatPath function to handle more cases
  const formatPath = (path) => {
    if (!path) return '/';
    if (typeof path !== 'string') {
      console.warn('formatPath received non-string input:', path);
      return '/';
    }
    // Clean up the path - ensure it starts with / and doesn't have double slashes
    let formattedPath = path.startsWith('/') ? path : `/${path}`;
    // Replace multiple slashes with a single slash
    formattedPath = formattedPath.replace(/\/+/g, '/');
    // Make sure it doesn't end with a slash unless it's just "/"
    if (formattedPath.length > 1 && formattedPath.endsWith('/')) {
      formattedPath = formattedPath.slice(0, -1);
    }
    return formattedPath;
  };

  const handleClearFilters = () => {
    setSelectedFolder(null);
    setFilterMode('all');
    setSelectedLanguage('');
  };

  return (
    <Box sx={{
      width: '100%',
      backgroundColor: darkMode ? 'background.default' : '#f5f5f5',
      minHeight: '100vh',
      padding: 0
    }}>
      <Paper sx={{
        borderRadius: 0,
        padding: 0
      }}>
        <PageHeader 
          darkMode={darkMode} 
          toggleDarkMode={toggleDarkMode}
          themeVariant={themeVariant} 
        />
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p:1 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            Home
          </Button>
          {/* Add arrow icon between buttons */}
          <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
          <Button
            variant="text"
            color="secondary"
            startIcon={<MonitorHeartIcon />}
            sx={{ mr: 2 }}
          >
            System
          </Button>
          <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
          <Button
            variant="text"
            color="secondary"
            startIcon={<LeaderboardIcon />}
            sx={{ mr: 2 }}
          >
            Editing Statistics
          </Button>
        </Box>
         <Box sx={{ mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
          <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 600 }}>
              Editing Statistics Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              View your editing activity and team leaderboard. Use filters to narrow insights by folder.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="filter-mode-label">Filter By</InputLabel>
                <Select
                  labelId="filter-mode-label"
                  value={filterMode}
                  label="Filter By"
                  onChange={(e) => setFilterMode(e.target.value)}
                >
                  <MenuItem value="all">All Data</MenuItem>
                  <MenuItem value="folder">Specific Folder</MenuItem>
                </Select>
              </FormControl>
              
              {filterMode === 'folder' && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="outlined"
                    startIcon={<FolderOpenIcon />}
                    onClick={() => setFolderBrowserOpen(true)}
                    size="medium"
                  >
                    {selectedFolder ? 'Change Folder' : 'Select Folder'}
                  </Button>
                  
                  {selectedFolder && (
                    <Chip
                      icon={<FolderIcon />}
                      label={selectedFolder.name || formatPath(selectedFolder.path || '')}
                      onDelete={handleClearFilters}
                      deleteIcon={<ClearIcon />}
                      variant="outlined"
                    />
                  )}
                </Stack>
              )}
              
              {filterMode !== 'all' && !selectedFolder && (
                <Typography variant="body2" color="text.secondary">
                  Please select a folder to filter statistics
                </Typography>
              )}

              {activeTab !== 4 && (
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel id="stats-date-range-label">Stats Range</InputLabel>
                  <Select
                    labelId="stats-date-range-label"
                    value={statsDateRange}
                    label="Stats Range"
                    onChange={(e) => setStatsDateRange(e.target.value)}
                  >
                    <MenuItem value="last24hours">Last 24 Hours</MenuItem>
                    <MenuItem value="last7days">Last 7 Days</MenuItem>
                    <MenuItem value="last30days">Last 30 Days</MenuItem>
                    <MenuItem value="last90days">Last 90 Days</MenuItem>
                    <MenuItem value="currentMonth">Current Month</MenuItem>
                    <MenuItem value="previousMonth">Previous Month</MenuItem>
                    <MenuItem value="all">All Available Data</MenuItem>
                  </Select>
                </FormControl>
              )}

              {activeTab === 1 && (
                <FormControl sx={{ minWidth: 220 }} disabled={languagesLoading || availableLanguages.length === 0}>
                  <InputLabel id="leaderboard-language-label">Languages</InputLabel>
                  <Select
                    labelId="leaderboard-language-label"
                    value={selectedLanguage}
                    label="Languages"
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                  >
                    <MenuItem value="">All Languages</MenuItem>
                    {availableLanguages.map((language) => (
                      <MenuItem key={language} value={language}>{language}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              {isAdmin && (
                <Tooltip title="Clear all tracking data">
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteSweepIcon />}
                    onClick={handleOpenClearDialog}
                    sx={{ mr: 2 }}
                  >
                    Clear Data
                  </Button>
                </Tooltip>
              )}
              
              <Tooltip title="Refresh statistics">
                <IconButton 
                  color="primary" 
                  onClick={handleRefresh}
                  disabled={refreshing}
                  sx={{ 
                    animation: refreshing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          </Paper>
          
          {/* Add filter controls */}
          
          
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              aria-label="editing statistics tabs"
            >
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 1 }} />
                    My Editing Stats
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LeaderboardIcon sx={{ mr: 1 }} />
                    Leaderboard
                  </Box>
                } 
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TimelineIcon sx={{ mr: 1 }} />
                    Stage Progression Pace
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TouchAppIcon sx={{ mr: 1 }} />
                    Feature Usage
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon sx={{ mr: 1 }} />
                    Trends
                  </Box>
                }
              />
            </Tabs>
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {activeTab === 0 && (
                <UserEditStats 
                  key={`user-stats-${refreshKey}`}
                  folderPath={filterMode === 'folder' && selectedFolder ? formatPath(selectedFolder.path) : null}
                  filterMode={filterMode}
                  dateRange={statsDateRange}
                />
              )}
              
              {activeTab === 1 && (
                <EditingLeaderboard 
                  key={`leaderboard-${refreshKey}`}
                  folderPath={filterMode === 'folder' && selectedFolder ? formatPath(selectedFolder.path) : null}
                  filterMode={filterMode}
                  dateRange={statsDateRange}
                  selectedLanguage={selectedLanguage}
                />
              )}

              {activeTab === 2 && (
                <StageTransitionAnalytics
                  key={`stage-progression-pace-${refreshKey}`}
                  folderPath={filterMode === 'folder' && selectedFolder ? formatPath(selectedFolder.path) : null}
                  selectedLanguage={selectedLanguage}
                />
              )}

              {activeTab === 3 && (
                <FeatureUsageStats
                  key={`feature-usage-${refreshKey}`}
                  dateRange={statsDateRange}
                  isAdmin={isAdmin}
                />
              )}

              {activeTab === 4 && (
                <EditTrendsTab key={`trends-${refreshKey}`} />
              )}
            </>
          )}
        </Box>
      </Paper>
      
      {/* Clear Tracking Data Confirmation Dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={handleCloseClearDialog}
        aria-labelledby="clear-tracking-dialog-title"
      >
        <DialogTitle id="clear-tracking-dialog-title" sx={{ color: 'error.main' }}>
          Clear All Tracking Data
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will permanently delete <strong>ALL editing tracking data</strong> from the database. This includes:
          </DialogContentText>
          
          <Box component="ul" sx={{ pl: 3 }}>
            <Box component="li" sx={{ mb: 1 }}>All user editing sessions</Box>
            <Box component="li" sx={{ mb: 1 }}>All editing statistics for all videos</Box>
            <Box component="li" sx={{ mb: 1 }}>All leaderboard information</Box>
            <Box component="li" sx={{ mb: 1 }}>All time tracking history</Box>
          </Box>
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. All tracking data will be permanently lost.
          </Alert>
          
          {clearError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {clearError}
            </Alert>
          )}
          
          {clearSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              All tracking data has been successfully cleared.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseClearDialog}
            disabled={clearingData}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            color="error"
            onClick={handleClearTrackingData}
            disabled={clearingData || clearSuccess}
            startIcon={clearingData ? <CircularProgress size={16} color="inherit" /> : <DeleteSweepIcon />}
          >
            {clearingData ? 'Clearing...' : 'Clear All Data'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Folder Browser Dialog */}
      <VideoBrowserDialog
        open={folderBrowserOpen}
        onClose={() => setFolderBrowserOpen(false)}
        onSelect={handleFolderSelect}
        title="Select Folder for Statistics"
        folderSelectOnly={true}
        showFolderSelectionButton={true}
      />
    </Box>
  );
};

export default EditingStatsPage;